import { AlertCircle, CheckCircle2, Loader2, ShieldAlert } from "lucide-react";

import type { AgentExecutionStep, AgentPlanStep } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { cn, formatDate } from "@/lib/utils";

interface AgentProgressStepProps {
  step: AgentPlanStep | AgentExecutionStep;
  mode: "plan" | "execution";
}

function statusVisual(status: string) {
  switch (status) {
    case "completed":
      return { icon: CheckCircle2, tone: "text-success" };
    case "failed":
      return { icon: AlertCircle, tone: "text-danger" };
    case "executing":
    case "running":
    case "processing":
      return { icon: Loader2, tone: "text-accent animate-spin" };
    default:
      return { icon: ShieldAlert, tone: "text-warning" };
  }
}

export function AgentProgressStep({ step, mode }: AgentProgressStepProps) {
  const { icon: Icon, tone } = statusVisual(step.status);
  const startedAt = mode === "execution" && "started_at" in step ? step.started_at : undefined;

  return (
    <div className="rounded-2xl border border-border/70 bg-background-secondary/55 p-3">
      <div className="flex items-start gap-3">
        <div className={cn("mt-0.5 shrink-0", tone)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-text-primary">{step.description}</p>
            <Badge className="tracking-[0.04em]" variant={step.status === "completed" ? "success" : step.status === "failed" ? "danger" : "warning"}>
              {step.status}
            </Badge>
            {"requires_approval" in step && step.requires_approval ? (
              <Badge className="tracking-[0.04em]" variant="secondary">
                Approval
              </Badge>
            ) : null}
          </div>

          {"tool_name" in step ? (
            <p className="text-xs uppercase tracking-[0.12em] text-text-tertiary">{step.tool_name.split("_").join(" ")}</p>
          ) : null}

          {step.error ? <p className="text-sm text-danger">{step.error}</p> : null}

          {startedAt ? (
            <p className="text-xs text-text-tertiary">Started {formatDate(startedAt, "MMM d, HH:mm:ss")}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
